import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Box, MenuItem, Select, InputLabel, FormControl, IconButton, Accordion, AccordionSummary, AccordionDetails, useTheme, useMediaQuery, Menu } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, query, getDocs, doc, getDoc, deleteDoc, orderBy } from 'firebase/firestore'; // Added getDoc
import { logDeleteOperation } from '../utils/logger'; // Import logging function
import { getISOWeek, startOfWeek, endOfWeek, subWeeks } from 'date-fns'; // Import date-fns functions
import jsPDF from 'jspdf'; // Import jsPDF
import { applyPlugin } from 'jspdf-autotable'; // Import applyPlugin
import * as XLSX from 'xlsx-js-style'; // Import xlsx-js-style
import logo from '../logo.png'; // Use the image file directly
import { ChartLegend, ChartTooltipContent } from './ui/chart';
import AnimatedBrewzLogo from './ui/AnimatedBrewzLogo'; // Import the animated logo
import { Link } from 'react-router-dom';
import { Edit, Delete, ExpandMore as ExpandMoreIcon, Menu as MenuIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday'; // Import the weekday plugin
import timezone from 'dayjs/plugin/timezone'; // Import the timezone plugin
import utc from 'dayjs/plugin/utc'; // Import the utc plugin
import { BarChart, Bar, PieChart, Pie, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'; // Import recharts components
import GreenBackground from './ui/Green_Background.jpg';

// Helper function to format dates as DD-MM-YYYY
const formatDateDDMMYYYY = (dateString) => {
  if (!dateString) return '';
  // Handles both Date objects and 'YYYY-MM-DD' strings
  return dayjs(dateString).format('DD-MM-YYYY');
};

dayjs.extend(weekday); // Extend dayjs with the weekday plugin
dayjs.extend(utc); // Extend dayjs with the utc plugin
dayjs.extend(timezone); // Extend dayjs with the timezone plugin

dayjs.tz.setDefault('Asia/Kolkata'); // Set default timezone to IST

applyPlugin(jsPDF); // Apply the plugin

// Styled components for consistent dark theme
const StyledDatePicker = styled(MuiDatePicker)(({ theme }) => ({
  '& .MuiInputBase-root': {
    color: '#fff',
    backgroundColor: '#333',
    borderRadius: theme.shape.borderRadius,
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    color: '#bbb',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
    // Add notch effect for the label
    '&.MuiInputLabel-shrink': {
      backgroundColor: '#333', // Match the input's background
      padding: '0 4px',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#555',
  },
  '& .MuiSvgIcon-root': {
    color: '#fff',
  },
}));

// Styled components for consistent dark theme
const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#17ffc4',
  color: '#000',
  '&:hover': {
    backgroundColor: '#14d9a6',
  },
}));

const StyledFormControl = styled(FormControl)({
  minWidth: 200,
  '& .MuiInputLabel-root': {
    color: '#bbb',
    '&.Mui-focused': {
      color: '#17ffc4',
    },
    // Add notch effect for the label
    '&.MuiInputLabel-shrink': {
      backgroundColor: '#333', // Match the input's background
      padding: '0 4px',
    },
  },
  '& .MuiSelect-root': {
    color: 'white',
    backgroundColor: '#333',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#555',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#17ffc4',
  },
  '& .MuiSvgIcon-root': {
    color: 'white',
  },
});

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: '#2a2a2a',
  border: '1px solid #444',
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 4px 12px 0 rgba(0,0,0,0.5)',
}));

const StyledTableCell = styled(TableCell)({
  color: '#fff',
  borderColor: '#444',
});

const StyledTableHeadCell = styled(StyledTableCell)({
  fontWeight: 'bold',
  backgroundColor: '#333',
});



// Banner component - Defined before Dashboard function
function Banner({ handleLogout, currentUser }) { // Accept handleLogout and currentUser as props
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Define shared nav button style for uniformity
  const dashboardNavBtnSx = {
    minWidth: { xs: 110, sm: 120 },
    fontSize: { xs: '0.92rem', sm: '1rem' },
    padding: '6px 0',
    borderRadius: '6px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    boxShadow: 2,
    textTransform: 'none',
    color: '#F3F8FF !important',
    mr: 1,
  };

  return (
    <Box sx={{
      backgroundImage: `linear-gradient(rgba(0, 0, 0, .8), rgba(0, 0, 0, 0.6)), url(${GreenBackground})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      color: '#fff',
      padding: { xs: '6px', sm: '10px' },
      marginBottom: '20px',
      borderRadius: '4px',
      position: 'sticky',
      top: 0,
      zIndex: 1100,
      display: 'flex',
      flexWrap: { xs: 'wrap', sm: 'nowrap' },
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: { xs: 1, sm: 2 },
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
        {/* Circular Logo */}
        <Box
          sx={{
            width: { xs: 36, sm: 50, md: 60 },
            height: { xs: 36, sm: 50, md: 60 },
            borderRadius: '50%',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mr: { xs: 1, sm: 2 },
          }}
        >
          <img src={logo} alt="Brewz Coffee Logo" style={{ width: '100%', height: 'auto' }} />
        </Box>
        <AnimatedBrewzLogo />
      </Box>

      {isMobile ? (
        <>
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={handleMenuOpen}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {currentUser && currentUser.email === 'admin@brewzcoffee.in' && (
              <>
                <MenuItem component={Link} to="/logs" onClick={handleMenuClose} sx={{ '&:hover': { backgroundColor: 'rgba(6, 208, 1, 0.1)' } }}>View Logs</MenuItem>
                <MenuItem component={Link} to="/gst-report" onClick={handleMenuClose} sx={{ '&:hover': { backgroundColor: 'rgba(6, 208, 1, 0.1)' } }}>GST Report</MenuItem>
                <MenuItem component={Link} to="/settlement" onClick={handleMenuClose} sx={{ '&:hover': { backgroundColor: 'rgba(6, 208, 1, 0.1)' } }}>Settlement</MenuItem>
                <MenuItem component={Link} to="/sales-history" onClick={handleMenuClose} sx={{ '&:hover': { backgroundColor: 'rgba(6, 208, 1, 0.1)' } }}>Sales History</MenuItem>
              </>
            )}
            <MenuItem onClick={() => { handleLogout(); handleMenuClose(); }} sx={{ color: '#FB4141', '&:hover': { backgroundColor: 'rgba(251, 65, 65, 0.1)' } }}><LogoutIcon sx={{ mr: 1 }} />Logout</MenuItem>
          </Menu>
        </>
      ) : (
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center' }}>
          {currentUser && currentUser.email === 'admin@brewzcoffee.in' && (
            <>
              <Button variant="outlined" component={Link} to="/logs" sx={{ ...dashboardNavBtnSx, color: '#F3F8FF', borderColor: '#F3F8FF', '&:hover': { backgroundColor: 'rgba(243, 248, 255, 0.1)', borderColor: '#06D001' } }}>View Logs</Button>
              <Button variant="outlined" component={Link} to="/gst-report" sx={{ ...dashboardNavBtnSx, color: '#F3F8FF', borderColor: '#F3F8FF', '&:hover': { backgroundColor: 'rgba(243, 248, 255, 0.1)', borderColor: '#06D001' } }}>GST Report</Button>
              <Button variant="outlined" component={Link} to="/settlement" sx={{ ...dashboardNavBtnSx, color: '#F3F8FF', borderColor: '#F3F8FF', '&:hover': { backgroundColor: 'rgba(243, 248, 255, 0.1)', borderColor: '#06D001' } }}>Settlement</Button>
              <Button variant="outlined" component={Link} to="/sales-history" sx={{ ...dashboardNavBtnSx, color: '#F3F8FF', borderColor: '#F3F8FF', '&:hover': { backgroundColor: 'rgba(243, 248, 255, 0.1)', borderColor: '#06D001' } }}>Sales History</Button>
            </>
          )}
          <IconButton onClick={handleLogout} sx={{ color: '#FB4141', '&:hover': { backgroundColor: 'rgba(251, 65, 65, 0.1)' } }} aria-label="logout">
            <LogoutIcon />
          </IconButton>
        </Box>
      )}

    </Box>
  );
}

function Dashboard() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [stockEntries, setStockEntries] = useState({});
  const [allStockEntries, setAllStockEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(dayjs(startOfWeek(new Date(), { weekStartsOn: 1 }))); 
  const [endDate, setEndDate] = useState(dayjs(endOfWeek(new Date(), { weekStartsOn: 1 }))); 
  const [selectedVendor, setSelectedVendor] = useState('');
  const [weeklyTrendData, setWeeklyTrendData] = useState([]); 
  // Helper function to get week date range (same as in Settlement.js)
  const getWeekDateRange = (week, year) => {
    const date = new Date(year, 0, 1 + (week - 1) * 7);
    const dayOfWeek = date.getDay();
    const start = new Date(date);
    start.setDate(date.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1)); // Start on Monday
    start.setHours(0, 0, 0, 0); // Set to start of day
    const end = new Date(start);
    end.setDate(start.getDate() + 6); // End on Sunday
    end.setHours(23, 59, 59, 999); // Set to end of day
    return { 
      start,
      end,
      startISO: start.toISOString(),
      endISO: end.toISOString()
    };
  };

  const [currentWeekSplitData, setCurrentWeekSplitData] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [currentWeekNumber] = useState(`${dayjs().year()}-W${dayjs().isoWeek()}`);
  const [weeklySalesData, setWeeklySalesData] = useState({
    totalSales: 0,
    cashSales: 0,
    tickets: 0,
    apc: 0,
    forecast: 0
  });



  const handleLogout = useCallback(async () => {
    try {
      await logout(); // Call the logout function from AuthContext
      navigate('/login'); // Navigate to the login page after successful logout
    } catch (error) {
      console.error('Failed to log out:', error);
      // Optionally, display an error message to the user
    }
  }, [logout, navigate]); // Add logout and navigate to dependency array

  const fetchStockEntries = useCallback(async () => {
    setLoading(true);
    let q;
    if (currentUser && currentUser.email === 'admin@brewzcoffee.in') {
      // Admin user fetches all entries
      q = query(collection(db, 'stockEntries'), orderBy('date'));
    } else {
      // Regular user fetches all entries (admin entries are now visible to all)
      q = query(collection(db, 'stockEntries'), orderBy('date'));
    }
    const querySnapshot = await getDocs(q);
    const allEntries = [];
    const uniqueVendors = new Set();
    querySnapshot.forEach((doc) => {
      const entry = { id: doc.id, ...doc.data() };
      // Calculate week number and add to entry
      const entryDate = new Date(entry.date);
      const weekNumber = getISOWeek(entryDate); // Use getISOWeek for ISO 8601 week numbering
      entry.weekNumber = `${entryDate.getFullYear()}-W${weekNumber}`;
      allEntries.push(entry);
      uniqueVendors.add(entry.vendorName);
    });
    setAllStockEntries(allEntries);
    setVendors(['All Vendors', ...Array.from(uniqueVendors).sort()]);
    setLoading(false);
  }, [currentUser]);

  // Set initial week number on component mount and load sales data
  // This useEffect fetches and calculates sales data for the ticker
  useEffect(() => {
    const loadWeeklySalesData = async () => {
      // Ensure we have valid dates before fetching
      if (!startDate || !endDate) return;

      // Use the same data source as SalesHistory.js (posDailySummaries)
      const dailySummariesCol = collection(db, 'posDailySummaries');
      const snapshot = await getDocs(dailySummariesCol);

      let totalSales = 0;
      let cashSales = 0;
      let tickets = 0;

      const startMoment = dayjs(startDate).startOf('day');
      const endMoment = dayjs(endDate).endOf('day');
      const historyCutoff = dayjs().subtract(3, 'year').startOf('day'); // consider only last 3 years for forecast

      // Buckets for historical weekday averages (0=Sunday ... 6=Saturday)
      const totalsByWeekday = Array(7).fill(0);
      const countsByWeekday = Array(7).fill(0);

      // Process each document
      snapshot.docs.forEach(doc => {
        const pos = { id: doc.id, ...doc.data() };

        // Parse date using the same logic as SalesHistory.js
        let saleDate;
        if (pos.date && typeof pos.date === 'object' && pos.date.toDate) {
          saleDate = dayjs(pos.date.toDate());
        } else if (pos.date) {
          saleDate = dayjs(pos.date);
        } else if (pos.createdAt && pos.createdAt.toDate) {
          saleDate = dayjs(pos.createdAt.toDate());
        } else {
          return; // Skip entries without a valid date
        }

        const saleTotal = Number(pos.totalSales) || 0;
        const saleCash = Number(pos.cashSales) || 0;
        const saleTickets = Number(pos.tickets) || 0;

        // Accumulate current selected week totals
        if (saleDate.isAfter(startMoment.subtract(1, 'day')) && saleDate.isBefore(endMoment.add(1, 'day'))) {
          totalSales += saleTotal;
          cashSales += saleCash;
          tickets += saleTickets;
        }

        // Accumulate for historical weekday averages (last 3 years only)
        const weekdayIndex = saleDate.day(); // 0 (Sun) - 6 (Sat)
        if (!isNaN(weekdayIndex) && saleDate.isAfter(historyCutoff.subtract(1, 'day'))) {
          totalsByWeekday[weekdayIndex] += saleTotal; // gross amount only
          countsByWeekday[weekdayIndex] += 1;
        }
      });

      // Calculate APC (Average Per Cover)
      const apc = tickets > 0 ? totalSales / tickets : 0;

      // Calculate weekly forecast = sum of average sales for each weekday (based on history)
      const avgByWeekday = totalsByWeekday.map((sum, idx) => (countsByWeekday[idx] ? sum / countsByWeekday[idx] : 0));
      const forecast = avgByWeekday.reduce((acc, v) => acc + v, 0);

      // Update state with weekly sales data
      setWeeklySalesData({
        totalSales,
        cashSales,
        tickets,
        apc,
        forecast
      });
    };

    loadWeeklySalesData();
  }, [startDate, endDate]); // Re-run when the date range changes

  const applyFilters = useCallback(() => {
    let filteredEntries = allStockEntries;

    // Filter by date range
    if (startDate) {
      const start = new Date(startDate);
      filteredEntries = filteredEntries.filter(entry => {
        const entryDate = new Date(entry.date);
        return entryDate >= start;
      });
    }
    if (endDate) {
      const end = new Date(endDate);
      filteredEntries = filteredEntries.filter(entry => {
        const entryDate = new Date(entry.date);
        return entryDate <= end;
      });
    }

    // Filter by vendor
    if (selectedVendor && selectedVendor !== 'All Vendors') {
      filteredEntries = filteredEntries.filter(entry => entry.vendorName === selectedVendor);
    }

    // Group by week number
    const groupedEntries = filteredEntries.reduce((acc, entry) => {
      const weekNumber = entry.weekNumber; // Use the calculated weekNumber
      if (!acc[weekNumber]) {
        acc[weekNumber] = [];
      }
      acc[weekNumber].push(entry);
      return acc;
    }, {});

    setStockEntries(groupedEntries);

    // --- Data Processing for Charts ---

    // 1. Weekly Trend Data (Last 5 Weeks)
    const today = new Date();
    const last5Weeks = Array.from({ length: 5 }).map((_, i) => {
      const weekStartDate = startOfWeek(subWeeks(today, i), { weekStartsOn: 1 });
      
      const weekNumber = getISOWeek(weekStartDate);
      const year = weekStartDate.getFullYear();
      return `${year}-W${weekNumber}`;
    }).reverse(); // Get the last 5 weeks and reverse to show chronologically

    const weeklyData = last5Weeks.map(week => {
      const entriesInWeek = allStockEntries.filter(entry => entry.weekNumber === week);
      const vendorSplit = entriesInWeek.reduce((acc, entry) => {
        acc[entry.vendorName] = (acc[entry.vendorName] || 0) + entry.amount;
        return acc;
      }, {});
      return { week, ...vendorSplit };
    });
    setWeeklyTrendData(weeklyData);

    // 2. Current Week Split Data (Pie Chart)
    const currentWeekNumber = `${new Date().getFullYear()}-W${getISOWeek(new Date())}`;
    const currentWeekEntries = allStockEntries.filter(entry => entry.weekNumber === currentWeekNumber);
    const currentWeekVendorSplit = currentWeekEntries.reduce((acc, entry) => {
      acc[entry.vendorName] = (acc[entry.vendorName] || 0) + entry.amount;
      return acc;
    }, {});

    const pieChartData = Object.keys(currentWeekVendorSplit).map(vendor => ({
      name: vendor,
      value: currentWeekVendorSplit[vendor],
    }));
    setCurrentWeekSplitData(pieChartData);

  }, [allStockEntries, startDate, endDate, selectedVendor]);

  useEffect(() => {
    fetchStockEntries();
  }, [fetchStockEntries]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]); // Added applyFilters to dependency array

  const handleDelete = async (id) => {
    try {
      // Get the document data before deletion for logging
      const docRef = doc(db, 'stockEntries', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const deletedData = docSnap.data();
        
        // Delete the document
        await deleteDoc(docRef);
        
        // Log the delete operation
        if (currentUser) {
          await logDeleteOperation(currentUser.uid, id, deletedData);
        }
        
        // Update state after deletion
        setAllStockEntries(prevEntries => prevEntries.filter(entry => entry.id !== id));
        // Re-apply filters to update the displayed entries and charts
        applyFilters();
      } else {
        console.error('Document does not exist');
      }
    } catch (error) {
      console.error('Error deleting document: ', error);
      // Handle error, maybe show a message to the user
    }
  };

  const handleDownloadExcel = () => {
    const data = Object.values(stockEntries).flat().map(entry => ({
  // Add imprest sale for export
  'Imprest Sale': entry.imprestSaleCash === 'No' ? 'No' : 'Yes',
      'Date': new Date(entry.date).toLocaleDateString('en-GB'),
      'Vendor Name': entry.vendorName,
      'Net Amount Paid': entry.amount,
      'Image URL': entry.imageUrl || 'N/A'
    }));

    if (data.length === 0) {
      alert('No data to export.');
      return;
    }

    const headerStyle = { 
      fill: { fgColor: { rgb: "C6E0B4" } }, // Light Green
      font: { bold: true },
      alignment: { horizontal: 'center', vertical: 'center' }
    };

    const setColumnWidths = (worksheet, data) => {
      if (!data || data.length === 0) return;
      const objectMaxLength = [];
      const header = Object.keys(data[0]);
      data.forEach(row => {
        header.forEach((key, i) => {
          const len = (row[key] || "").toString().length;
          objectMaxLength[i] = Math.max(objectMaxLength[i] || 0, len);
        });
      });
      worksheet['!cols'] = header.map((h, i) => ({ 
        wch: Math.max(h.length, objectMaxLength[i]) + 2
      }));
    };

    const styleHeaders = (worksheet) => {
      if (!worksheet['!ref']) return;
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ c: C, r: range.s.r });
        if (worksheet[address]) {
          worksheet[address].s = headerStyle;
        }
      }
    };

    const ws = XLSX.utils.json_to_sheet(data);

    styleHeaders(ws);
    setColumnWidths(ws, data);

    // Format currency column
    data.forEach((row, index) => {
      const rowIndex = index + 2; // +1 for header, +1 for 1-based index
      const cellAddress = `D${rowIndex}`; // 'Imprest Sale' is the 4th column (index 3)
      if(ws[cellAddress]) {
        ws[cellAddress].s = {
          font: { bold: true },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }
      const cellAddressAmount = `C${rowIndex}`; // 'Net Amount Paid' is the 3rd column (index 2)
      if(ws[cellAddressAmount]) {
        ws[cellAddressAmount].t = 'n';
        ws[cellAddressAmount].z = '₹#,##0.00';
      }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Entries');
    XLSX.writeFile(wb, 'stock_entries.xlsx');
  };

  const handleDownloadPdf = (selectedDateRange) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const body = Object.values(stockEntries).flat().map(entry => ({
      date: new Date(entry.date).toLocaleDateString('en-GB'),
      vendorName: entry.vendorName || 'N/A',
      amount: Number(entry.amount || 0).toLocaleString(),
      weekNumber: entry.weekNumber,
    }));

    if (body.length === 0) {
      alert('No data to export.');
      return;
    }

    const weekNumber = body.length > 0 ? body[0].weekNumber : '';
    const title = `Invoices for the Week: ${weekNumber}`;

    // Add logo
    // Use a small, fixed logo size and position for testing

    // Debug: log the logo variable before adding to PDF
    console.log('PDF logo base64 (first 100 chars):', logo ? logo.substring(0, 100) : logo);
    // Removed logo from PDF export as per user request
    // doc.addImage(logo, 'JPEG', logoX, logoY, logoWidth, logoHeight);

    // Add Title
    doc.setFontSize(18);
    doc.text(title, 14, 22);

    // Add Subtitle with date range
    doc.setFontSize(11);
    doc.setTextColor(100);
    const dateRange = selectedDateRange.start && selectedDateRange.end
      ? `${dayjs(selectedDateRange.start).format('DD MMM YYYY')} - ${dayjs(selectedDateRange.end).format('DD MMM YYYY')}`
      : 'All Time';
    doc.text(`Date Range: ${dateRange}`, 14, 35);

    doc.autoTable({
      columns: [
        { header: 'Date', dataKey: 'date' },
        { header: 'Vendor Name', dataKey: 'vendorName' },
        { header: 'Net Amount Paid', dataKey: 'amount' },
      ],
      body: body,
      startY: 50,
      headStyles: { fillColor: [198, 224, 180], textColor: [0, 0, 0], fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 2, rowHeight: 15 },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 'auto', halign: 'right' },
      },
    });

    doc.save('stock_entries.pdf');
  };

  const handleAddEntry = () => {
    navigate('/entry');
  };

  const totalAmount = Object.values(stockEntries)
    .flat()
    .reduce((sum, entry) => sum + (entry.amount || 0), 0);

  const weekNumbers = Object.keys(stockEntries).sort();
  let weekTitlePart = '';
  if (weekNumbers.length === 1) {
    const weekPart = weekNumbers[0].split('-W')[1];
    weekTitlePart = ` - Week ${weekPart}`;
  } else if (weekNumbers.length > 1) {
    const firstWeek = weekNumbers[0].split('-W')[1];
    const lastWeek = weekNumbers[weekNumbers.length - 1].split('-W')[1];
    weekTitlePart = ` - Weeks ${firstWeek}-${lastWeek}`;
  }

  // The currentWeekNumber is already set in the state and will be used for the pie chart

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      {/* --- Current Week Sales Summary --- */}
      {/* --- Marquee Sales Summary --- */}
      

      {/* Reusable Banner for consistent look */}
      <Banner handleLogout={handleLogout} currentUser={currentUser} />

      {/* Marquee Sales Summary - moved immediately below Banner */}
      {currentWeekNumber && (() => {
        // Get the current week's date range
        const weekNum = parseInt(currentWeekNumber.split('-W')[1], 10);
        const year = parseInt(currentWeekNumber.split('-W')[0], 10);
        const { start: weekStart, end: weekEnd } = getWeekDateRange(weekNum, year);
        // Format the date range for display (e.g., "Jul 15 - Jul 21")


const formatDate = (date) => {
          if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            return 'Invalid Date';
          }
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        };
        const dateRange = `${formatDate(weekStart)} - ${formatDate(weekEnd)}`;
        const { tickets, totalSales, apc, forecast } = weeklySalesData;
        const summaryText = `Weekly Sales (${dateRange}) | Tickets: ${tickets} | Gross: ₹${totalSales.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | APC: ₹${apc.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Forecasted: ₹${(forecast || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const achievedPct = forecast > 0 ? (totalSales / forecast) * 100 : 0;
        const isUp = achievedPct >= 100;
        const arrowSymbol = isUp ? '▲' : '▼';
        const arrowColor = isUp ? '#06D001' : '#FB4141';
        return (
          <Box sx={{
            background: 'linear-gradient(135deg, rgba(14, 16, 13, 0.9),rgba(0, 0, 0, 0.4))',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)', // For Safari
            color: '#F3F8FF',
            p: 1,
            borderRadius: 2,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            border: '2px solid rgb(0, 0, 0)',
            my: 2,
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.5)',
            MozOpacity: 0.5,
          }}>
            <Box
              sx={{
                display: 'inline-block',
                pl: '100%',
                animation: 'marquee 20s linear infinite',
                '@keyframes marquee': {
                  '0%': { transform: 'translateX(0%)' },
                  '100%': { transform: 'translateX(-100%)' },
                },
                '& .MuiTypography-root': { // Apply text shadow to all typography children
                  textShadow: '1px 1px 3px rgba(0,0,0,0.5)',
                }
              }}
            >
              <Typography variant="h6" component="span" sx={{ mx: 4 }}>
                {summaryText}
                {` | Achieved: `}
                <span style={{ color: arrowColor, fontWeight: 700 }}>
                  {`${arrowSymbol} ${achievedPct.toFixed(0)}%`}
                </span>
              </Typography>
            </Box>
          </Box>
        );
      })()}
      <Container>
        <Typography variant="h3" gutterBottom sx={{ textAlign: 'center' }}>Invoice Dashboard</Typography>

        {/* Add New Entry Button */}
        <Box sx={{ display: 'flex', width: '100%', mb: 2 }}>
          <Button 
            variant="contained" 
            onClick={handleAddEntry} 
            fullWidth
            sx={{
              backgroundColor: '#06D001',
              color: '#000',
              '&:hover': { 
                backgroundColor: '#9BEC00',
              },
              fontWeight: 600,
            }}
          >
            Add New Entry +
          </Button>
        </Box>

        {/* Charts Section - Responsive Mobile Layout */}
        <Box
          sx={theme => ({
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            gap: 2,
            mb: 2,
            width: '100%',
            minHeight: { xs: 'auto', md: '400px' },
            '& > *': {
              flex: '1 1 0',
              minWidth: { xs: '100%', md: '50%' },
              maxWidth: { md: 'calc(50% - 8px)' },
              height: '100%'
            }
          })}
        >
          {/* 5-Week Vendor Trends Bar Chart */}
          <Paper sx={{
            p: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid #9BEC00',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: 300, md: 450 },
            height: '100%',
            mb: { xs: 2, md: 0 },
          }}>
            <Typography variant="h6" sx={{ color: '#fff', mb: 2, textAlign: 'center', fontSize: { xs: '1.05rem', md: '1.25rem' } }}>5-Week Purchase Trend</Typography>
            <Box sx={{ width: '100%', flexGrow: 1, minHeight: { xs: 220, md: 300 }, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weeklyTrendData} margin={{ top: 5, right: 20, left: -10, bottom: 40 }}>
                  <XAxis dataKey="week" stroke="#ccc" angle={-45} textAnchor="end" interval={0} style={{ fontSize: '10px' }} tickFormatter={(tick) => tick.substring(tick.indexOf('-') + 1)} dy={10} />
                  <YAxis stroke="#ccc" style={{ fontSize: '11px' }} />
                  <Tooltip content={<ChartTooltipContent />} cursor={{ fill: 'rgba(2, 152, 10, 0.47)' }} />
                  {vendors.filter(v => v !== 'All Vendors').map((vendor, index) => (
                    <Bar key={vendor} dataKey={vendor} stackId="a" fill={COLORS[index % COLORS.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ mt: 'auto' }}>
              <ChartLegend config={vendors.filter(v => v !== 'All Vendors').reduce((acc, v, i) => ({ ...acc, [v]: { label: v, color: COLORS[i % COLORS.length] } }), {})} className="dashboard-legend" />
            </Box>
          </Paper>

          {/* Current Week Vendor Distribution Pie Chart */}
          <Paper sx={{
            p: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid #9BEC00',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: 300, md: 450 },
            height: '100%',
          }}>
            <Typography variant="h6" sx={{ color: '#fff', mb: 2, textAlign: 'center', fontSize: { xs: '1.05rem', md: '1.25rem' } }}>Current Week Vendor Split</Typography>
            <Box sx={{ width: '100%', flexGrow: 1, minHeight: { xs: 220, md: 300 }, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={currentWeekSplitData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={'60%'} outerRadius={'80%'} labelLine={false} label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}>
                    {currentWeekSplitData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ mt: 'auto' }}>
              <ChartLegend config={currentWeekSplitData.reduce((acc, item, i) => ({ ...acc, [item.name]: { label: item.name, color: COLORS[i % COLORS.length] } }), {})} className="dashboard-legend" />
            </Box>
          </Paper>
        </Box>

        {/* Responsive Legend Font Size */}
        <style>{`
          .dashboard-legend .MuiTypography-root {
            font-size: 0.89rem;
          }
          @media (max-width: 600px) {
            .dashboard-legend .MuiTypography-root {
              font-size: 0.78rem !important;
              margin-right: 4px;
            }
            .dashboard-legend {
              gap: 4px !important;
              row-gap: 2px !important;
            }
          }
        `}</style>

        {/* Filter Controls - now stacked vertically on mobile */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: '20px',
          marginBottom: '20px',
          flexWrap: 'wrap',
        }}>
          {/* Date Range Pickers */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StyledDatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              maxDate={endDate || undefined}
              format="DD-MM-YYYY"
              slotProps={{ textField: { fullWidth: true } }}
            />
            <StyledDatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              minDate={startDate || undefined}
              maxDate={dayjs().endOf('day')}
              format="DD-MM-YYYY"
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>

          {/* Vendor Filter */}
          <StyledFormControl sx={{ flex: 1 }}>
            <InputLabel id="vendor-select-label">Vendor</InputLabel>
            <Select
              labelId="vendor-select-label"
              id="vendor-select"
              value={selectedVendor}
              label="Vendor"
              onChange={(e) => setSelectedVendor(e.target.value)}
            >
              {vendors.map((vendor) => (
                <MenuItem key={vendor} value={vendor}>{vendor}</MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </Box>

        {/* Stock Entries Accordion */}
        <Accordion defaultExpanded sx={{ backgroundColor: '#2a2a2a', color: '#fff', border: '1px solid #9BEC00', borderRadius: 2, boxShadow: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
                Invoice Entries{weekTitlePart}
              </Typography>
              <Typography variant="h6">Total: ₹{totalAmount.toLocaleString()}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            {loading ? (
              <Typography sx={{ padding: 2 }}>Loading entries...</Typography>
            ) : Object.keys(stockEntries).length === 0 ? (
              <Typography sx={{ padding: 2 }}>No entries found for the selected period and vendor.</Typography>
            ) : (
              <StyledTableContainer component={Paper} sx={{ border: 'none', borderRadius: 0 }}>
                <Table sx={{ minWidth: 650 }} aria-label="stock entries table">
                  <TableHead>
                    <TableRow>
                      <StyledTableHeadCell>Week</StyledTableHeadCell>
                      <StyledTableHeadCell>Date</StyledTableHeadCell>
                      <StyledTableHeadCell>Vendor Name</StyledTableHeadCell>
                      <StyledTableHeadCell>Net Amount Paid</StyledTableHeadCell>
<StyledTableHeadCell>Imprest Sale</StyledTableHeadCell>
                      <StyledTableHeadCell>Image</StyledTableHeadCell>
                      <StyledTableHeadCell>Actions</StyledTableHeadCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.values(stockEntries).flat().map((entry) => (
                      <TableRow 
                        key={entry.id} 
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          ...(entry.imprestSaleCash === 'No' && { backgroundColor: 'rgba(255, 70, 70, 0.15)' }),
                        }}
                      >
                        <StyledTableCell component="th" scope="row">
                          {entry.weekNumber}
                        </StyledTableCell>
                        <StyledTableCell>{formatDateDDMMYYYY(entry.date)}</StyledTableCell>
                        <StyledTableCell>{entry.vendorName}</StyledTableCell>
                        <StyledTableCell align="right">₹{entry.amount}</StyledTableCell>
<StyledTableCell>{entry.imprestSaleCash === 'No' ? 'No' : 'Yes'}</StyledTableCell>
                        <StyledTableCell>
                          {entry.imageUrl ? (
                            <a href={entry.imageUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#17ffc4' }}>View Image</a>
                          ) : (
                            'N/A'
                          )}
                        </StyledTableCell>
                        <StyledTableCell>
                          <IconButton size="small" onClick={() => navigate(`/entry/${entry.id}`)} sx={{ color: '#17ffc4', mr: 1 }}>
                            <Edit />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(entry.id)} sx={{ color: '#ff5252' }}>
                            <Delete />
                          </IconButton>
                        </StyledTableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            )}
          </AccordionDetails>
        </Accordion>


        {/* Download Buttons */}
        <Box sx={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
          <Button 
            variant="contained" 
            onClick={handleDownloadExcel} 
            sx={{ 
              backgroundColor: '#06D001',
              color: '#000',
              '&:hover': { 
                backgroundColor: '#9BEC00',
              },
              fontWeight: 600,
              mr: 1
            }}
          >
            Download Excel
          </Button>
          <StyledButton 
            variant="contained" 
            onClick={() => handleDownloadPdf({ start: startDate, end: endDate })} 
            
            sx={{ 
              backgroundColor: '#E14434',
              color: '#000',
              '&:hover': { 
                backgroundColor: '#87281F',
              },
              fontWeight: 600,
              mr: 1
            }}
          >
            Download PDF
          </StyledButton>
        </Box>
      </Container>
    </Box>
  );
}

// Define COLORS array for charts using a monochromatic green theme
const COLORS = [
  '#333333', // Main brand green
  '#4D4D4D', // Lighter brand green
  '#666666', // Darker shade
  '#808080', // Lighter shade
  '#999999', // Lightest shade (from GST Report)
  '#B3B3B3', // Muted green (from GST Report)
  '#CCCCCC', // Dark, muted green
  '#E0E0E0', // Bright lime green
  '#F5F5F5', // Very dark green
];

export default Dashboard;